/**
 * English and Arabic, as two flat dictionaries with identical key sets.
 *
 * Flat rather than nested on purpose: a missing key is then a compile error via
 * the Keys type below, instead of a silent `undefined` that renders as blank. The
 * Accounting app carries ~130 keys in the same shape and it has held up.
 *
 * Arabic notes that matter here:
 *  - NUMBERS STAY WESTERN (0-9), not Arabic-Indic. Kuwaiti business documents and
 *    accounting systems use Western digits, and the invoices these modules read are
 *    printed that way. Localising digits would make figures disagree with the paper.
 *  - Money stays "KWD 1.234" with three decimals in both languages.
 *  - Terminology follows Kuwaiti accounting usage: مطابقة for reconciliation,
 *    فاتورة for invoice, قيد for a journal entry.
 */

export const EN = {
  // Brand and chrome
  "app.name": "PetPoint",
  "app.tagline": "PetPoint operations",
  "app.company": "PetPoint Technical",

  // Login
  "login.title": "Sign in",
  "login.email": "Email",
  "login.password": "Password",
  "login.submit": "Sign in",
  "login.submitting": "Signing in…",
  "login.failed": "Could not sign in. Please try again.",

  // Session / chrome
  "nav.signOut": "Sign out",
  "nav.collapse": "Collapse sidebar",
  "nav.expand": "Expand sidebar",
  "nav.modules": "Modules",
  "nav.loading": "Loading",
  "theme.dark": "Dark mode",
  "lang.switch": "العربية",
  "lang.label": "Language",

  // Dashboard
  "dash.welcome": "Welcome, {name}",
  "dash.signedInAs": "You are signed in as {email}.",
  "dash.roleOne": "Your role is {roles}.",
  "dash.roleMany": "Your roles are {roles}.",
  "dash.mustChange": "Your password was set by an administrator. Please change it.",
  "dash.yourModules": "Your modules",
  "dash.noModules": "No modules are available to you yet.",
  "dash.permissions": "Permissions",
  "dash.permissionsHelp":
    "What you may do. Each is checked by the server on every request — this list describes that, it is not the mechanism.",
  "dash.open": "Open",

  // Admin shell
  "admin.users": "Users",
  "admin.roles": "Roles",
  "admin.audit": "Audit log",
  "admin.title": "Administration",

  // Users
  "users.title": "Users",
  "users.add": "Add user",
  "users.name": "Name",
  "users.email": "Email",
  "users.roles": "Roles",
  "users.lastSignIn": "Last sign-in",
  "users.never": "never",
  "users.status": "Status",
  "users.active": "active",
  "users.deactivated": "deactivated",
  "users.deactivate": "Deactivate",
  "users.reactivate": "Reactivate",
  "users.revokeNote":
    "Deactivating a user revokes their live sessions immediately, rather than waiting for the cookie to expire.",
  "users.newTitle": "New user",
  "users.fullName": "Full name",
  "users.tempPassword": "Temporary password (at least 12 characters)",
  "users.tempPasswordHelp": "The user must change this at first sign-in.",
  "users.create": "Create",
  "users.cancel": "Cancel",
  "users.loadFailed": "Could not load users",
  "users.updateFailed": "Could not update the user",
  "users.rolesFailed": "Could not change roles",
  "users.createFailed": "Could not create the user",
  "users.role": "Role",
  "users.uuid": "ID",
  "users.edit": "Edit",
  "users.close": "Close",
  "users.save": "Save changes",
  "users.saving": "Saving…",
  "users.saved": "Saved",
  "users.identity": "Identity",
  "users.newPassword": "New password",
  "users.newPasswordHelp": "Leave blank to keep the current one. Changing it signs the person out everywhere.",
  "users.emailHelp": "This is their sign-in name. Changing it signs them out everywhere.",
  "users.access": "Access",
  "users.accessHelp": "Their role decides what they can do. These adjust what they can reach and change — per person, without inventing a new role.",
  "users.features": "Fixed by the role",
  "users.featuresHelp": "These come with {role} and cannot be changed for one person.",
  "users.fromRole": "From role",
  "users.granted": "Granted",
  "users.revoked": "Revoked",
  "users.effective": "In effect",
  "users.notInEffect": "Not in effect",
  // The checkbox grid: which systems and sections one person reaches.
  "users.revert": "Revert",
  "users.unsaved": "Unsaved: {what}",
  "users.theProfile": "profile",
  "users.notSaved": "Not saved: {systems}",
  "users.colSection": "Section",
  "users.colRead": "Read",
  "users.colWrite": "Write",
  "users.systemClosed": "No access to this system",
  "users.saveSection": "Save",
  "users.sectionsOn": "{on} of {total}",
  "users.administersAnalysis": "This person administers Analysis, which carries every section below. Clearing a box here will not take it away — remove the Analysis administrator grant first.",
  // Under the Sales system, and the warning for a page without a shop.
  "users.salesHelp": "Daily Form Write takes effect for the Rai and Qurain shop accounts, at their own shop, and together with All shops for anyone, at the shop they pick on the page. Grooming Write works the same way. Without All shops, only a shop account or a groomer sees a shop's data.",
  "users.salesNoShop": "Tick “All shops” as well. Without it these pages open for this person with no shop in them: only a shop account or a groomer has a shop of their own.",
  // After a save whose result is not what the boxes asked for.
  "users.savedDiffers": "Saved, but {systems} came back different from the ticks: something this screen does not draw still grants part of it. The boxes now show what this person really holds.",
  "users.impliedBy": "Held anyway, because {by} requires it",
  "users.copyId": "Copy ID",
  "users.copied": "Copied",
  "users.deleteUser": "Delete",
  "users.deleteConfirm": "Delete {name}? Their audit history is kept.",
  "users.deleted": "Deleted {name}",
  "users.deleteFailed": "Could not delete the user",

  // Roles
  "roles.title": "Roles and permissions",
  "roles.help":
    "A user may hold several roles and their permissions add up. The administrator role cannot be edited — it is the recovery path if another role is misconfigured.",
  "roles.permission": "Permission",
  "roles.userCount": "{n} users",
  "roles.adminLocked": "The administrator role always has everything",
  "roles.saved": "Saved {name}",
  "roles.loadFailed": "Could not load roles",
  "roles.saveFailed": "Could not change permissions",

  // Audit
  "audit.title": "Audit log",
  "audit.help":
    "Append-only. Nothing here is ever edited or removed, including by an administrator — that is what makes it worth reading.",
  "audit.when": "When",
  "audit.who": "Who",
  "audit.action": "Action",
  "audit.entity": "Entity",
  "audit.ip": "IP",
  "audit.diff": "Diff",
  "audit.hide": "Hide",
  "audit.before": "Before",
  "audit.after": "After",
  "audit.system": "system",
  "audit.loadFailed": "Could not load the audit log",
  "audit.filters": "Filters",
  "audit.search": "Search",
  "audit.searchPlaceholder": "Person, action, or record id",
  "audit.module": "Module",
  "audit.actor": "Person",
  "audit.from": "From",
  "audit.to": "To",
  "audit.hideLogins": "Hide sign-ins",
  "audit.clear": "Clear filters",
  "audit.all": "All",
  "audit.showing": "Showing {n} of {total}",
  "audit.showingAll": "{total} entries",
  "audit.loadOlder": "Load older",
  "audit.loading": "Loading…",
  "audit.noResults": "Nothing matches these filters.",
  "audit.capped": "Capped at {limit} per page — narrow the filters or load older.",

  // Permission denied
  "denied.title": "Not available to you",
  "denied.body":
    "This page needs the {permission} permission. Ask an administrator if you need it.",

  // Modules (fallbacks; the server supplies name and name_ar)
  "module.dashboard": "Dashboard",
  "module.accounting": "Reconciliation",
  "module.admin": "Administration",
  "module.grooming": "Sales",
} as const;

export type Keys = keyof typeof EN;

export const AR: Record<Keys, string> = {
  "app.name": "بتبوينت",
  "app.tagline": "عمليات بتبوينت",
  "app.company": "بتبوينت التقنية",

  "login.title": "تسجيل الدخول",
  "login.email": "البريد الإلكتروني",
  "login.password": "كلمة المرور",
  "login.submit": "تسجيل الدخول",
  "login.submitting": "جارٍ تسجيل الدخول…",
  "login.failed": "تعذر تسجيل الدخول. حاول مرة أخرى.",

  "nav.signOut": "تسجيل الخروج",
  "nav.collapse": "تصغير الشريط الجانبي",
  "nav.expand": "توسيع الشريط الجانبي",
  "nav.modules": "الوحدات",
  "nav.loading": "جارٍ التحميل",
  "theme.dark": "الوضع الليلي",
  "lang.switch": "English",
  "lang.label": "اللغة",

  "dash.welcome": "مرحباً، {name}",
  "dash.signedInAs": "أنت مسجل الدخول باسم {email}.",
  "dash.roleOne": "دورك هو {roles}.",
  "dash.roleMany": "أدوارك هي {roles}.",
  "dash.mustChange": "تم تعيين كلمة المرور من قبل المسؤول. يرجى تغييرها.",
  "dash.yourModules": "وحداتك",
  "dash.noModules": "لا توجد وحدات متاحة لك بعد.",
  "dash.permissions": "الصلاحيات",
  "dash.permissionsHelp":
    "ما يمكنك فعله. يتحقق الخادم من كل صلاحية في كل طلب — هذه القائمة وصف لذلك وليست الآلية نفسها.",
  "dash.open": "فتح",

  "admin.users": "المستخدمون",
  "admin.roles": "الأدوار",
  "admin.audit": "سجل التغييرات",
  "admin.title": "الإدارة",

  "users.title": "المستخدمون",
  "users.add": "إضافة مستخدم",
  "users.name": "الاسم",
  "users.email": "البريد الإلكتروني",
  "users.roles": "الأدوار",
  "users.lastSignIn": "آخر دخول",
  "users.never": "لم يسجل بعد",
  "users.status": "الحالة",
  "users.active": "نشط",
  "users.deactivated": "معطّل",
  "users.deactivate": "تعطيل",
  "users.reactivate": "إعادة التنشيط",
  "users.revokeNote":
    "تعطيل المستخدم يُنهي جلساته النشطة فوراً، دون انتظار انتهاء صلاحية الكوكي.",
  "users.newTitle": "مستخدم جديد",
  "users.fullName": "الاسم الكامل",
  "users.tempPassword": "كلمة مرور مؤقتة (١٢ حرفاً على الأقل)",
  "users.tempPasswordHelp": "يجب على المستخدم تغييرها عند أول تسجيل دخول.",
  "users.create": "إنشاء",
  "users.cancel": "إلغاء",
  "users.loadFailed": "تعذر تحميل المستخدمين",
  "users.updateFailed": "تعذر تحديث المستخدم",
  "users.rolesFailed": "تعذر تغيير الأدوار",
  "users.createFailed": "تعذر إنشاء المستخدم",
  "users.role": "الدور",
  "users.uuid": "المعرّف",
  "users.edit": "تعديل",
  "users.close": "إغلاق",
  "users.save": "حفظ التغييرات",
  "users.saving": "جارٍ الحفظ…",
  "users.saved": "تم الحفظ",
  "users.identity": "البيانات",
  "users.newPassword": "كلمة مرور جديدة",
  "users.newPasswordHelp": "اتركها فارغة للإبقاء على الحالية. تغييرها ينهي جلساته في كل الأجهزة.",
  "users.emailHelp": "هذا اسم الدخول. تغييره ينهي جلساته في كل الأجهزة.",
  "users.access": "الصلاحيات",
  "users.accessHelp": "الدور يحدد ما يستطيع فعله. هذه تحدد ما يمكنه الوصول إليه وتغييره — لكل شخص، دون إنشاء دور جديد.",
  "users.features": "ثابتة بحسب الدور",
  "users.featuresHelp": "هذه تأتي مع {role} ولا يمكن تغييرها لشخص واحد.",
  "users.fromRole": "من الدور",
  "users.granted": "ممنوحة",
  "users.revoked": "ملغاة",
  "users.effective": "سارية",
  "users.notInEffect": "غير سارية",
  "users.revert": "تراجع",
  "users.unsaved": "غير محفوظ: {what}",
  "users.theProfile": "الملف الشخصي",
  "users.notSaved": "لم يُحفظ: {systems}",
  "users.colSection": "القسم",
  "users.colRead": "عرض",
  "users.colWrite": "تعديل",
  "users.systemClosed": "لا صلاحية لهذا النظام",
  "users.saveSection": "حفظ",
  "users.sectionsOn": "{on} من {total}",
  "users.administersAnalysis": "هذا الشخص يدير وحدة التحليل، وهي تشمل كل الأقسام أدناه. إلغاء تحديد مربع هنا لن يلغيها — أزل صلاحية إدارة التحليل أولاً.",
  "users.salesHelp": "صلاحية «تعديل» في Daily Form تسري لحسابَي محلَّي الري والقرين على محلّهما، ومع All shops لأي شخص على المحل الذي يختاره في الصفحة. وصلاحية «تعديل» في Grooming تعمل بالطريقة نفسها. بدون All shops لا يرى بيانات أي محل إلا حساب المحل أو المُجمِّل.",
  "users.salesNoShop": "حدِّد «All shops» أيضاً. بدونها تُفتح هذه الصفحات لهذا الشخص دون أي محل فيها: وحدهما حساب المحل والمُجمِّل لهما محل خاص بهما.",
  "users.savedDiffers": "تم الحفظ، لكن {systems} عادت مختلفة عمّا حُدِّد: شيء لا تعرضه هذه الشاشة ما زال يمنح جزءاً منها. المربعات تعرض الآن ما يملكه هذا الشخص فعلاً.",
  "users.impliedBy": "سارية لأن {by} تتطلبها",
  "users.copyId": "نسخ المعرّف",
  "users.copied": "تم النسخ",
  "users.deleteUser": "حذف",
  "users.deleteConfirm": "حذف {name}؟ سيبقى سجل تغييراته محفوظاً.",
  "users.deleted": "تم حذف {name}",
  "users.deleteFailed": "تعذر حذف المستخدم",

  "roles.title": "الأدوار والصلاحيات",
  "roles.help":
    "يمكن للمستخدم أن يحمل عدة أدوار وتتجمع صلاحياتها. لا يمكن تعديل دور المسؤول — فهو طريق الاستعادة إذا أُسيء إعداد دور آخر.",
  "roles.permission": "الصلاحية",
  "roles.userCount": "{n} مستخدم",
  "roles.adminLocked": "دور المسؤول يملك كل الصلاحيات دائماً",
  "roles.saved": "تم حفظ {name}",
  "roles.loadFailed": "تعذر تحميل الأدوار",
  "roles.saveFailed": "تعذر تغيير الصلاحيات",

  "audit.title": "سجل التغييرات",
  "audit.help":
    "للإضافة فقط. لا يُعدّل أو يُحذف أي شيء هنا، ولا حتى من قبل المسؤول — وهذا ما يجعله جديراً بالقراءة.",
  "audit.when": "التاريخ",
  "audit.who": "المستخدم",
  "audit.action": "الإجراء",
  "audit.entity": "العنصر",
  "audit.ip": "عنوان الشبكة",
  "audit.diff": "الفروق",
  "audit.hide": "إخفاء",
  "audit.before": "قبل",
  "audit.after": "بعد",
  "audit.system": "النظام",
  "audit.loadFailed": "تعذر تحميل سجل التغييرات",
  "audit.filters": "عوامل التصفية",
  "audit.search": "بحث",
  "audit.searchPlaceholder": "شخص، إجراء، أو رقم سجل",
  "audit.module": "الوحدة",
  "audit.actor": "الشخص",
  "audit.from": "من",
  "audit.to": "إلى",
  "audit.hideLogins": "إخفاء تسجيلات الدخول",
  "audit.clear": "إزالة التصفية",
  "audit.all": "الكل",
  "audit.showing": "يظهر {n} من {total}",
  "audit.showingAll": "{total} سجل",
  "audit.loadOlder": "تحميل الأقدم",
  "audit.loading": "جارٍ التحميل…",
  "audit.noResults": "لا يوجد ما يطابق هذه التصفية.",
  "audit.capped": "الحد {limit} لكل صفحة — ضيّق التصفية أو حمّل الأقدم.",

  "denied.title": "غير متاح لك",
  "denied.body": "تحتاج هذه الصفحة إلى صلاحية {permission}. راجع المسؤول إذا كنت بحاجة إليها.",

  "module.dashboard": "لوحة التحكم",
  "module.accounting": "المطابقة",
  "module.admin": "الإدارة",
  "module.grooming": "المبيعات",
};

export const DICTIONARIES = { en: EN, ar: AR } as const;
export type Lang = keyof typeof DICTIONARIES;
export const LANGS: Lang[] = ["en", "ar"];
export const RTL_LANGS = new Set<Lang>(["ar"]);
